import * as React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useMutation } from "react-query";
import { LoadingButton } from "@mui/lab";
import {
  ApplicationEventSubscriptionsService,
  ApplicationEventType,
  ApplicationEventNotificationTemplateType,
  Branch
} from "gen/clients";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { supportedEventTypeOptions } from "utils/applicationEventUtils";

interface Props {
  open: boolean;
  onClose: () => void;
  branches: Branch[];
  onSuccess: () => void;
}

enum FieldName {
  EVENT_TYPE = "eventType",
  BRANCH_ID = "branchId",
  EMAILS = "emails",
  NOTES = "notes"
}

interface FormValues {
  [FieldName.EVENT_TYPE]: ApplicationEventType | "";
  [FieldName.BRANCH_ID]: string;
  [FieldName.EMAILS]: string;
  [FieldName.NOTES]: string;
}

const validationSchema = Yup.object({
  [FieldName.EVENT_TYPE]: Yup.string().required("Event type is required"),
  [FieldName.BRANCH_ID]: Yup.string().required("Branch is required"),
  [FieldName.EMAILS]: Yup.string()
    .required("Emails are required")
    .test("valid-emails", "Please enter valid comma-separated email addresses", value => {
      if (!value) {
        return false;
      }
      const emailList = value
        .split(",")
        .map(e => e.trim())
        .filter(e => e.length > 0);
      if (emailList.length === 0) {
        return false;
      }
      const emailRegex = /^[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}$/;
      return emailList.every(email => emailRegex.test(email));
    }),
  [FieldName.NOTES]: Yup.string()
});

const CreateEventSubscriptionDialog: React.FC<Props> = ({ open, onClose, branches, onSuccess }) => {
  const branchOptions = React.useMemo(() => {
    const options = branches
      .map(branch => ({
        label: branch.name,
        value: branch.id
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    // Add "All" option at the beginning
    return [{ label: "All", value: "ALL" }, ...options];
  }, [branches]);

  const createMutation = useMutation(
    (values: FormValues) => {
      const emailList = values[FieldName.EMAILS]
        .split(",")
        .map(e => e.trim())
        .filter(e => e.length > 0);
      const eventType = values[FieldName.EVENT_TYPE];
      if (eventType === "") {
        throw new Error("Event type is required");
      }
      return ApplicationEventSubscriptionsService.createApplicationEventSubscription({
        requestBody: {
          eventType,
          notificationTemplateType: ApplicationEventNotificationTemplateType.INTERNAL,
          clientId: "ALL",
          branchId: values[FieldName.BRANCH_ID],
          emails: emailList,
          notes: values[FieldName.NOTES] || undefined
        }
      });
    },
    {
      onSuccess: () => {
        onSuccess();
        onClose();
      }
    }
  );

  const initialValues: FormValues = {
    [FieldName.EVENT_TYPE]: "",
    [FieldName.BRANCH_ID]: "ALL",
    [FieldName.EMAILS]: "",
    [FieldName.NOTES]: ""
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Event Subscription</DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={values => createMutation.mutate(values)}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {createMutation.error && <ApiErrorMessage apiError={createMutation.error} />}

                <SelectOneField
                  name={FieldName.EVENT_TYPE}
                  label="Event Type"
                  options={supportedEventTypeOptions}
                  required
                />

                <SelectOneField name={FieldName.BRANCH_ID} label="Branch" options={branchOptions} required />

                <TextInputField
                  name={FieldName.EMAILS}
                  label="Notification Emails"
                  required
                  helperText="Enter comma-separated email addresses"
                />

                <TextInputField name={FieldName.NOTES} label="Notes" />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                Create
              </LoadingButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export { CreateEventSubscriptionDialog };
